import { useI18n } from '../i18n/useI18n'
import type { AppStatus } from '../types'
import { AdSlot } from './AdSlot'
import {
  MIOSEGNO_DISPLAY_SIZES,
  MIOSEGNO_LANDSCAPE_COMPACT_BANNER_MAPPING,
  MIOSEGNO_LANDSCAPE_COMPACT_BANNER_SIZES,
  MIOSEGNO_RESPONSIVE_SIZE_MAPPING,
  mioSegnoAdPlacementDefinition,
  type MioSegnoAdPlacement,
} from '../ads/adPlacement'

/**
 * Existing mioSegno ad-policy boundary.
 *
 * `eligible` is still decided by the existing mioSegno policy
 * (`ownerAdsEnabled`, `playback.showAds`, share policy, etc.). This component
 * does not reimplement entitlement logic; it only renders inventory that the
 * existing policy has already declared eligible.
 *
 * v0.15.57 uses the provider-neutral AdSlot in deployment builds as well as
 * DEV. The concrete 2n4uAd provider chooses DR or PROD at runtime by hostname,
 * preserving one exact frontend artifact for DR -> PROD promotion.
 */
export function NonPersonalizedAdSlot({
  eligible,
  placement,
  compactBanner = false,
}: {
  status: AppStatus | null
  eligible: boolean
  placement: MioSegnoAdPlacement
  compactBanner?: boolean
}) {
  const { t } = useI18n()
  if (!eligible) return null

  const inventory = mioSegnoAdPlacementDefinition(placement)
  const sizes = compactBanner
    ? MIOSEGNO_LANDSCAPE_COMPACT_BANNER_SIZES
    : MIOSEGNO_DISPLAY_SIZES
  const responsive = compactBanner
    ? MIOSEGNO_LANDSCAPE_COMPACT_BANNER_MAPPING
    : MIOSEGNO_RESPONSIVE_SIZE_MAPPING

  return (
    <aside className="nonpersonalized-ad-slot" aria-label={t('ads.aria')}>
      <span className="ad-label">{t('ads.label')}</span>
      <AdSlot
        adUnitId={inventory.adUnitId}
        sizes={sizes}
        responsive={responsive}
        position={inventory.position}
        surface={inventory.surface}
        className="miosegno-provider-ad-host"
        style={{ maxWidth: '100%', overflow: 'hidden' }}
      />
    </aside>
  )
}
